import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { BlockForm, OrchardMap } from '../../components/orchard';
import { Layers, Loader2 } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { Block, Farm, GeoJSONPolygon } from '../../types/orchard';
import { toNum } from '@/utils/numeric';

function BlockEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [block, setBlock] = useState<Block | null>(null);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);

  const blockId = Number(id);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const blockResponse = await orchardService.getBlock(blockId);
      if (!blockResponse.success || !blockResponse.data) {
        setError('Failed to load block');
        return;
      }
      const blockData = blockResponse.data;
      setBlock(blockData);
      setPolygon(blockData.boundary_geometry);
      setAreaFromMap(toNum(blockData.area_hectares));

      const farmResponse = await orchardService.getFarm(blockData.farm_id);
      if (farmResponse.success && farmResponse.data) {
        setFarm(farmResponse.data);
      }
    } catch {
      setError('Failed to load block. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [blockId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handleSubmit = async (fieldData: {
    name: string;
    description?: string;
    area_hectares?: number;
    active?: boolean;
  }) => {
    if (!polygon || !block) return;

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.updateBlock(blockId, {
        farm_id: block.farm_id,
        name: fieldData.name,
        description: fieldData.description,
        boundary_geometry: polygon,
        center_point: centerPoint,
        area_hectares: fieldData.area_hectares ?? areaFromMap ?? undefined,
        active: fieldData.active,
      });

      if (response.success) {
        navigate(`/orchard/blocks/${blockId}`);
      } else {
        setError((response as any).message || 'Failed to update block');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Block"
          subtitle="Loading..."
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error && !block) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Block"
          subtitle="Error"
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="outline" onClick={loadData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Edit: ${block?.name || 'Block'}`}
        subtitle={farm ? `Block in ${farm.name}` : 'Update block details and boundary'}
        icon={<Layers className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-4">Block Details</h2>
              {block && (
                <BlockForm
                  initialValues={{
                    name: block.name,
                    description: block.description ?? undefined,
                    area_hectares: toNum(block.area_hectares) ?? undefined,
                    active: block.active,
                  }}
                  areaFromMap={areaFromMap}
                  hasPolygon={!!polygon}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate(`/orchard/blocks/${blockId}`)}
                  isSubmitting={isSubmitting}
                />
              )}
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-sm text-blue-800 font-medium">
                Drag the vertices to adjust the block boundary. On desktop, use Save to confirm
                changes. On mobile, use the Finish button. You can also draw a new polygon from scratch.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                farmBoundary={farm?.boundary_geometry}
                blocks={[]}
                drawMode="block"
                editPolygon={block?.boundary_geometry}
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                center={
                  block?.center_point
                    ? [block.center_point.lat, block.center_point.lng]
                    : farm?.center_point
                    ? [farm.center_point.lat, farm.center_point.lng]
                    : undefined
                }
                zoom={16}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlockEdit;
